/**
 * Action bindings — persisted in localStorage.
 * v2 defaults use real in-browser actions (toast, beep, grab→ask, etc.).
 */

import type { ActionBinding } from "../engines/types";
import { presetToThen } from "./actionCatalog";
import { loadCustomGestures } from "./customGestures";

const KEY = "visionforge.bindings.v2";
const LEGACY_KEY = "visionforge.bindings.v1";

export const DEFAULT_BINDINGS: ActionBinding[] = [
  {
    id: "b-open-palm",
    when: { engine: "gesture", gesture: "Open_Palm", minConfidence: 0.55 },
    then: presetToThen("toast"),
    enabled: true,
  },
  {
    id: "b-fist",
    when: { engine: "gesture", gesture: "Closed_Fist", minConfidence: 0.55 },
    then: presetToThen("toggle_mappings"),
    enabled: true,
    fireWhenPaused: true,
  },
  {
    id: "b-point",
    when: { engine: "gesture", gesture: "Pointing_Up", minConfidence: 0.55 },
    then: presetToThen("scroll_up"),
    enabled: true,
  },
  {
    id: "b-victory",
    when: { engine: "gesture", gesture: "Victory", minConfidence: 0.55 },
    then: presetToThen("grab_to_ask"),
    enabled: true,
  },
  {
    id: "b-thumb-up",
    when: { engine: "gesture", gesture: "Thumb_Up", minConfidence: 0.55 },
    then: presetToThen("speak"),
    enabled: true,
  },
  {
    id: "b-thumb-down",
    when: { engine: "gesture", gesture: "Thumb_Down", minConfidence: 0.55 },
    then: presetToThen("scroll_down"),
    enabled: true,
  },
  {
    id: "b-pinch",
    when: { engine: "gesture", gesture: "Pinch", minConfidence: 0.55 },
    then: presetToThen("beep"),
    enabled: true,
  },
  {
    id: "b-ily",
    when: { engine: "gesture", gesture: "ILoveYou", minConfidence: 0.55 },
    then: presetToThen("flash_stage"),
    enabled: true,
  },
];

function ensureAllGestures(list: ActionBinding[]): ActionBinding[] {
  const known = new Set(DEFAULT_BINDINGS.map((b) => b.when.gesture));
  const have = new Set(list.map((b) => b.when.gesture));
  const next = [...list];
  for (const def of DEFAULT_BINDINGS) {
    if (def.when.gesture && !have.has(def.when.gesture) && known.has(def.when.gesture)) {
      next.push({ ...def });
    }
  }
  return next;
}

export function loadBindings(): ActionBinding[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ActionBinding[];
      if (Array.isArray(parsed) && parsed.length) {
        return ensureAllGestures(parsed);
      }
    }
    // Drop legacy v1 (mostly noop emit/hotkey) so new defaults apply once
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
  return DEFAULT_BINDINGS.map((b) => ({ ...b }));
}

export function saveBindings(list: ActionBinding[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function resetBindings(): ActionBinding[] {
  const next = DEFAULT_BINDINGS.map((b) => ({ ...b }));
  saveBindings(next);
  return next;
}

/** Add a binding row for a newly taught gesture (toast default). */
export function bindingForCustomGesture(name: string): ActionBinding {
  return {
    id: `b-custom-${name}`,
    when: { engine: "gesture", gesture: name, minConfidence: 0.55 },
    then: presetToThen("toast"),
    enabled: true,
  };
}

export function upsertGestureBinding(
  list: ActionBinding[],
  name: string,
): ActionBinding[] {
  if (list.some((b) => b.when.gesture === name)) return list;
  return [...list, bindingForCustomGesture(name)];
}

export function removeGestureBinding(
  list: ActionBinding[],
  name: string,
): ActionBinding[] {
  return list.filter((b) => b.when.gesture !== name);
}

/** Export JSON for ./data/bindings shelf (ops HF sync). */
export function exportBindingsJson(list: ActionBinding[]): string {
  return JSON.stringify(
    {
      version: 3,
      updatedAt: Date.now(),
      bindings: list,
      customGestures: loadCustomGestures(),
    },
    null,
    2,
  );
}
